import {
  Calendar,
  Check,
  CheckCircle2,
  Clock,
  Fuel,
  Loader2,
  MapPin,
  Navigation,
  Sparkles,
  Wrench,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { trpc } from '../../lib/trpc';
import type { CandidateSlotFit, Job, OperatorProfile } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';

export interface JobSlotFinderDialogProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
  unassignedJobs?: Job[];
  operators?: OperatorProfile[];
  onSlotSelected?: (serviceDate: string, operatorProfileId: number) => void;
}

export function JobSlotFinderDialog({
  job: initialJob,
  isOpen,
  onClose,
  unassignedJobs = [],
  operators: _operators = [],
  onSlotSelected,
}: JobSlotFinderDialogProps) {
  const utils = trpc.useUtils();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(initialJob?.id ?? null);
  const activeJob =
    unassignedJobs.find((j) => j.id === selectedJobId) || initialJob || unassignedJobs[0] || null;

  const today = new Date().toISOString().split('T')[0];
  const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];

  const [activeTab, setActiveTab] = useState<'probe' | 'scanner'>('probe');
  const [testDate, setTestDate] = useState<string>(activeJob?.serviceDate || today);
  const [testWindowPreset, setTestWindowPreset] = useState<string>('morning');
  const [preferredWindow, setPreferredWindow] = useState<'anytime' | 'morning' | 'afternoon'>(
    'anytime'
  );
  const [isBooking, setIsBooking] = useState(false);

  // Compute test start/end offsets
  let windowStart: number | null = null;
  let windowEnd: number | null = null;
  if (testWindowPreset === 'morning') {
    windowStart = 28800; // 08:00
    windowEnd = 43200; // 12:00
  } else if (testWindowPreset === 'afternoon') {
    windowStart = 43200; // 12:00
    windowEnd = 61200; // 17:00
  } else if (testWindowPreset === 'early_afternoon') {
    windowStart = 43200; // 12:00
    windowEnd = 54000; // 15:00
  }

  // 1. Single Date Feasibility Probe
  const { data: probeData, isFetching: isProbing } = trpc.jobPlan.checkSlotAvailability.useQuery(
    {
      jobId: activeJob?.id,
      latitude: activeJob?.latitude ?? undefined,
      longitude: activeJob?.longitude ?? undefined,
      estimatedDurationSeconds: activeJob?.estimatedDurationSeconds ?? 3600,
      requiredSkillIds: (activeJob?.requiredSkills || []).map((s) => s.id),
      serviceDate: testDate,
      windowStartOffsetSeconds: windowStart,
      windowEndOffsetSeconds: windowEnd,
    },
    {
      enabled: isOpen && activeTab === 'probe' && Boolean(activeJob) && Boolean(testDate),
    }
  );

  // 2. Horizon Best Slots Scanner
  const { data: scannerData, isFetching: isScanning } = trpc.jobPlan.findBestSlots.useQuery(
    {
      jobId: activeJob?.id,
      latitude: activeJob?.latitude ?? undefined,
      longitude: activeJob?.longitude ?? undefined,
      estimatedDurationSeconds: activeJob?.estimatedDurationSeconds ?? 3600,
      requiredSkillIds: (activeJob?.requiredSkills || []).map((s) => s.id),
      startDate: today,
      endDate: nextWeek,
      preferredWindow,
      limit: 4,
    },
    {
      enabled: isOpen && activeTab === 'scanner' && Boolean(activeJob),
    }
  );

  const assignJobMutation = trpc.jobPlan.assignJob.useMutation();
  const updateJobMutation = trpc.jobPlan.jobs.update.useMutation();

  const handleBookSlot = async (slot: CandidateSlotFit) => {
    if (!activeJob) return;
    setIsBooking(true);
    try {
      // 1. Update job date & window
      await updateJobMutation.mutateAsync({
        id: activeJob.id,
        serviceDate: slot.serviceDate,
        windowStartOffsetSeconds: windowStart,
        windowEndOffsetSeconds: windowEnd,
      });

      // 2. Assign to chosen operator
      await assignJobMutation.mutateAsync({
        jobId: activeJob.id,
        operatorProfileId: slot.operatorProfileId,
        serviceDate: slot.serviceDate,
      });

      utils.jobPlan.getDaySchedule.invalidate();
      utils.jobPlan.jobs.list.invalidate();
      onSlotSelected?.(slot.serviceDate, slot.operatorProfileId);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsBooking(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden flex flex-col max-h-[85vh] bg-background">
        <DialogHeader className="p-4 border-b border-border/50 bg-card/80 backdrop-blur shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold">
                Capacity & Slot Availability Finder
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Instantly check route capacity, skill match, and minimal added drive time across
                your operators.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Target Job Selector / Info Card */}
          {activeJob ? (
            <div className="p-3 rounded-xl border border-border/50 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
              <div className="space-y-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-foreground truncate">{activeJob.title}</span>
                  <Badge variant="secondary" className="text-[10px]">
                    {Math.round(activeJob.estimatedDurationSeconds / 60)}m service
                  </Badge>
                  {activeJob.requiredSkills && activeJob.requiredSkills.length > 0 && (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <Wrench className="h-2.5 w-2.5" />
                      {activeJob.requiredSkills.map((s) => s.name).join(', ')}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground truncate">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                  <span className="truncate">{activeJob.formattedAddress}</span>
                </div>
              </div>

              {unassignedJobs.length > 1 && (
                <div className="shrink-0">
                  <select
                    className="h-7 rounded-md border border-input bg-background px-2 text-[11px] text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                    value={selectedJobId ?? activeJob.id}
                    onChange={(e) => setSelectedJobId(Number(e.target.value))}
                  >
                    {unassignedJobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.title} ({j.serviceDate})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          ) : (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Select or open a job to check availability.
            </div>
          )}

          {/* Mode Switcher */}
          <div className="flex p-1 bg-muted/40 rounded-xl border border-border/50 text-xs">
            <button
              type="button"
              onClick={() => setActiveTab('probe')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg font-medium transition-all ${
                activeTab === 'probe'
                  ? 'bg-background text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Clock className="h-3.5 w-3.5 text-primary" />
              <span>Test Specific Day / Window</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('scanner')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-lg font-medium transition-all ${
                activeTab === 'scanner'
                  ? 'bg-background text-foreground shadow-xs border border-border/60'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Zap className="h-3.5 w-3.5 text-amber-500" />
              <span>Suggest Best Slots This Week</span>
            </button>
          </div>

          {/* Mode 1 Content: Test Specific Day */}
          {activeTab === 'probe' && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-xl border border-border/50 bg-card">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Candidate Service Date</Label>
                  <Input
                    type="date"
                    value={testDate}
                    onChange={(e) => setTestDate(e.target.value)}
                    className="h-8 text-xs bg-background"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Time Window</Label>
                  <select
                    className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                    value={testWindowPreset}
                    onChange={(e) => setTestWindowPreset(e.target.value)}
                  >
                    <option value="anytime">Flexible (Anytime during shift)</option>
                    <option value="morning">Morning (08:00 – 12:00)</option>
                    <option value="early_afternoon">Early Afternoon (12:00 – 15:00)</option>
                    <option value="afternoon">Afternoon (12:00 – 17:00)</option>
                  </select>
                </div>
              </div>

              {/* Operator Results Feed */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-foreground px-1">
                  <span>Operator Availability ({probeData?.operatorOptions.length ?? 0})</span>
                  {isProbing && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                </div>

                <div className="space-y-2">
                  {(probeData?.operatorOptions || []).map((fit) => (
                    <div
                      key={fit.operatorProfileId}
                      className={`p-3 rounded-xl border transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs ${
                        fit.status === 'ideal'
                          ? 'border-emerald-500/30 bg-emerald-500/5'
                          : fit.status === 'tight'
                            ? 'border-amber-500/30 bg-amber-500/5'
                            : 'border-border/40 bg-muted/10 opacity-70'
                      }`}
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">{fit.operatorName}</span>
                          {fit.status === 'ideal' ? (
                            <Badge
                              variant="secondary"
                              className="text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                            >
                              <Check className="h-2.5 w-2.5 mr-0.5" />
                              Optimal Fit
                            </Badge>
                          ) : fit.status === 'tight' ? (
                            <Badge
                              variant="secondary"
                              className="text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300"
                            >
                              Tight Route
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-[10px] text-destructive border-destructive/30"
                            >
                              Conflict
                            </Badge>
                          )}
                        </div>

                        {fit.isFeasible ? (
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                            {fit.estimatedArrivalSeconds != null && (
                              <span className="flex items-center gap-1 font-medium text-foreground">
                                <Clock className="h-3 w-3 text-primary" />
                                Est. Arrival: {formatOffsetTime(fit.estimatedArrivalSeconds)}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Navigation className="h-3 w-3 text-amber-500 rotate-45" />+
                              {Math.round(fit.addedDriveSeconds / 60)}m drive (
                              {(fit.addedDriveMeters / 1000).toFixed(1)} km)
                            </span>
                            <span>Stop #{fit.insertionStopIndex + 1}</span>
                          </div>
                        ) : (
                          <p className="text-[11px] text-destructive/80">
                            {fit.violationReason || 'Not feasible for this operator.'}
                          </p>
                        )}
                      </div>

                      {fit.isFeasible && (
                        <Button
                          size="sm"
                          className="h-7 text-xs px-3 gap-1 shrink-0"
                          onClick={() => handleBookSlot(fit)}
                          disabled={isBooking}
                        >
                          {isBooking ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-3 w-3" />
                          )}
                          <span>Book Slot</span>
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Mode 2 Content: Horizon Best Slots Scanner */}
          {activeTab === 'scanner' && (
            <div className="space-y-3">
              <div className="p-3 rounded-xl border border-border/50 bg-card flex items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-foreground">Next 7 Days Horizon</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Preferred Time:</span>
                  <select
                    className="h-7 rounded-md border border-input bg-background px-2 text-xs text-foreground focus:outline-hidden focus:ring-1 focus:ring-ring"
                    value={preferredWindow}
                    onChange={(e) =>
                      setPreferredWindow(e.target.value as 'anytime' | 'morning' | 'afternoon')
                    }
                  >
                    <option value="anytime">Anytime</option>
                    <option value="morning">Morning (8am - 12pm)</option>
                    <option value="afternoon">Afternoon (12pm - 5pm)</option>
                  </select>
                </div>
              </div>

              {/* Recommended Top Slots */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-semibold text-foreground px-1">
                  <span>Top Recommended Slots</span>
                  {isScanning && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
                </div>

                {scannerData?.slots.length === 0 ? (
                  <div className="p-6 text-center text-xs text-muted-foreground border rounded-xl bg-card">
                    No available capacity found for this week given the job duration and operator
                    skills.
                  </div>
                ) : (
                  (scannerData?.slots || []).map((slot, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl border border-primary/30 bg-primary/5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                    >
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="secondary"
                            className="text-[10px] bg-primary/20 text-primary font-bold"
                          >
                            Option {idx + 1}
                          </Badge>
                          <span className="font-bold text-foreground text-sm">
                            {slot.serviceDate}
                          </span>
                          <span className="text-muted-foreground">• {slot.operatorName}</span>
                        </div>

                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          {slot.estimatedArrivalSeconds != null && (
                            <span className="font-semibold text-foreground">
                              Arrival: ~{formatOffsetTime(slot.estimatedArrivalSeconds)}
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                            <Fuel className="h-3 w-3" />+{(slot.addedDriveMeters / 1000).toFixed(1)}{' '}
                            km added travel
                          </span>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        className="h-8 text-xs px-3 gap-1.5 shrink-0"
                        onClick={() => handleBookSlot(slot)}
                        disabled={isBooking}
                      >
                        {isBooking ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        <span>Confirm Slot</span>
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-3 border-t border-border/50 bg-card/50 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
